import { Component, ChangeDetectorRef } from '@angular/core';
import { Config } from '@config';
import { UrlEnhancerService } from '@url';
import DataSource from 'devextreme/data/data_source';
import { DxAspNetDataXsrfAwareService } from '@tools';
import { MessagesService, MessageType } from '@ui-messages';

import { LicenseService } from './license.service';
import { PropertyConverter } from '@converter';
import { __i18n } from '@localisation';
import { PrivilegesService } from '@privileges';
import { RoutePrivilegeNames } from '@route-privilege-names';


@Component({
    selector: 'license-details',
    templateUrl: './license-details.template.html',
    standalone: false
})
export class LicenseDetailsComponent {
    constructor(
        private config : Config,
        private urlEnhancer : UrlEnhancerService,
        private dxAspNetXsrfAwareService : DxAspNetDataXsrfAwareService,
        private changeDetectorRef : ChangeDetectorRef,
        public service : LicenseService,
        private messageService : MessagesService,
        private propertyConverter : PropertyConverter,
        private privilegeService: PrivilegesService
    ){
        const privileges = this.privilegeService.getPrivileges();
        this.hasPrivSystemAdministrator = privileges.find(p => p.name === RoutePrivilegeNames.SystemAdministrator) !== undefined;
    }

    public newActivationId: string = '';

    public isPopUpVisible: boolean = false;

    public source: DataSource;

    public data: any;

    public activationKey: string = '';

    public licensedFeatures: Array<string> = null;

    public availableFeatures: Array<string> = null;

    public newFeatures: Array<string> = null;

    public showLoader: boolean = false;

    public hasPrivSystemAdministrator : boolean = false;

    public remoteOperations = {
        paging: true, filtering: true, sorting: true, grouping: true, summary: true, groupPaging: true
    };

    private async reload() {
        const data: any = await this.service.getActivationId();
        data.licensedFeaturesFormatted = (data.licensedFeatures || []).join(', ') || __i18n('<none>', 'license.details.nolicensedfeatures');
        this.data = data;
        this.licensedFeatures = this.data.licensedFeatures || [];
        this.activationKey = this.data.activationKey;
    }

    public async ngAfterViewInit(){
        await this.reload();
        let url = this.config.services.license.licenseKeyList;
        url = this.urlEnhancer.replaceParams(url);
        this.source = new DataSource({
            paginate: true,
            store: this.dxAspNetXsrfAwareService.createStore({
                loadUrl: url,
                loadMethod: 'POST',
                key: 'plantPurchasedFeatureLicenseId'
            })
        });
        this.changeDetectorRef.detectChanges();
    }

    public startDownload(){
        this.service.downloadActivationKey();
    }

    public openAddActivationId(){
        this.isPopUpVisible = true;
        this.newActivationId = '';
        this.messageService.deleteTargetMessages(this.messageTarget);
    }

    private messageTarget : string = 'popup-messages';

    private handleActivationIdValidationError(e){
        let errorText: string = null;
        if(typeof e.error === 'string' &&  e.error.startsWith('{')) {
            e.error = JSON.parse(e.error);
        }

        if(e.error && e.error.error && e.error.error.errorCode === 0) {
            errorText = __i18n('The request is invalid: Invalid activation id', 'license.list.invalid-activation-id.error.message')
        }
        if(e.error && e.error.error && e.error.error.errorCode === -1) {
            errorText = __i18n('This activation id does not contain any supported plant features.', 'license.details.message.nosupportedplantfeatures');
        }
        if(e.status === 404){
            errorText = __i18n('The given activation id was not found.', 'license.details.message.not-found-activation-key.message');
        }
        if(e.status === 409){
            errorText = __i18n('The given activation id is not a valid for the license management system.', 'license.details.message.not-valid-activation-key.message');
        }
        if(e.error && e.error.error && e.error.error.errorText) {
            errorText = e.error.error.errorText;
        }

        if(typeof e.error === 'string') {
            errorText = e.error;
        }

        this.messageService.addMessage({
            text : errorText || e.error?.Message || e.error || e.statusText,
            type: MessageType.error,
            target: this.messageTarget
        })
    }



    private handleActivationIdAddError(e){
        let errorText: string = null;
        if(typeof e.error === 'string' &&  e.error.startsWith('{')) {
            e.error = JSON.parse(e.error);
        }

        if(e.status === 404){
            errorText = __i18n(
                'The given activation id was not found.',
                'license.details.message.not-found-activation-key.message'
            );
        }
        if(e.status === 409){
            errorText = __i18n(
                'The given activation id is not a valid for the license management system.',
                'license.details.message.not-valid-activation-key.message'
            );
        }
        if(e?.error?.error?.errorCode) {
            errorText = this.service.getErrorTextFromCode(e.error.error.errorCode);
        }
       
        if(e?.error?.error?.errorText) {
            errorText = e.error.error.errorText;
        }

        if(typeof e.error === 'string') {
            errorText = e.error;
        }

        this.messageService.addMessage({
            text : errorText || e.error?.Message || e.error || e.statusText,
            type: MessageType.error,
            target: this.messageTarget
        })
    }

    private handleActivationIdValidationSuccess(response){
        this.availableFeatures = response.features;
        this.newFeatures = this.availableFeatures.filter(f => (this.licensedFeatures || []).findIndex(lf => lf === f) < 0);
        const hasNewFeatures = this.newFeatures.length > 0;
        this.messageService.addMessage({
            text: hasNewFeatures
                ? __i18n('This activation id contains the following features: ', 'license.details.message.containsfeature') + response.features.join(', ')
                : __i18n('All contained features are already licensed: ', 'license.details.message.allfeatureslicensed') + response.features.join(', '),
            type: hasNewFeatures
                ? MessageType.success
                : MessageType.info,
            target: this.messageTarget
        });
    }

    public async validateActivationId() {
        this.availableFeatures = [];
        this.newFeatures = [];
        this.showLoader = true;
        try {
            const value = (this.newActivationId) ? this.newActivationId.trim() : '';
            const response = await this.service.validateActivationId(this.activationKey, value);
            if (response.state !== 'ReadyForActivation') {
                this.messageService.addMessage({
                    text: __i18n('This activation id cannot be activated.', 'license.details.message.cannotbeactivated'),
                    type: MessageType.error,
                    target: this.messageTarget
                });
            } else {
               this.handleActivationIdValidationSuccess(response);
            }
        } catch (e) {
            this.handleActivationIdValidationError(e);
        } finally {
            this.showLoader = false;
        }
    }

    public async addActivationId(){
        this.showLoader = true;
        try {
            const value = (this.newActivationId) ? this.newActivationId.trim() : '';
            await this.service.addNewActivationId(this.activationKey, value);
            this.newActivationId = '';
            this.newFeatures = [];
            this.availableFeatures = [];
            this.isPopUpVisible = false;
            this.source.reload();
            await this.reload();
        } catch(e) {
            this.handleActivationIdAddError(e);
        } finally {
            this.showLoader = false;
        }
    }
}